// src/components/PerformanceMetricsChart.jsx
import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useSelector } from "react-redux"
import { Link } from "react-router-dom"
import axios from "axios"
import config from "config/config"
import FilterAltOutlinedIcon from "@mui/icons-material/FilterAltOutlined"
import Spinner from "reactstrap/lib/Spinner"

const CHART_HEIGHT = 320 // fixed drawing area so all bars share a baseline

export default function PerformanceMetricsChart({
  title = "Category Score",
  viewMode,
  fromDate,
  toDate,
}) {
  const mode = useSelector(state => state.Layout.layoutMode)
  const activeOrganization = useSelector(
    state => state.organizationReducer.activeOrganization,
  )
  const userData = useSelector(state => state.Login.userData)

  const [isLoading, setIsLoading] = useState(false)
  const [categories, setCategories] = useState([])
  const [selectedCategories, setSelectedCategories] = useState([])
  const [open, setOpen] = useState(false)

  const getColorForCategory = index => {
    // single tone until a palette is desired
    const colors = ["#2990DF", "#2990DF"]
    return colors[index % colors.length]
  }

  const fetchCategoryScores = useCallback(async () => {
    if (!activeOrganization) return
    try {
      setIsLoading(true)
      const response = await axios.get(
        `${config.backendUrl}/organization/getOrganizationCategoryScore/${activeOrganization.id}`,
        {
          headers: { Authorization: `Bearer ${userData.token}` },
          params: { viewMode, fromDate, toDate },
          skipLoader: true,
        },
      )

      const scores = response?.data?.categoryScores || []
      const categoryData = scores.map((item, index) => ({
        id: index,
        name: item.categoryName,
        currentScore: Number(Number(item.currentScore).toFixed(1)),
        color: getColorForCategory(index),
      }))

      setCategories(categoryData)
      setSelectedCategories(categoryData.map(i => i.name))
    } catch (err) {
      console.error("Error fetching category scores:", err)
    } finally {
      setIsLoading(false)
    }
  }, [activeOrganization, userData?.token, viewMode, fromDate, toDate])

  useEffect(() => {
    fetchCategoryScores()
  }, [fetchCategoryScores])

  const toggleCategory = categoryName => {
    setSelectedCategories(prev => {
      if (prev.includes(categoryName)) {
        if (prev.length === 1) return prev // keep at least one selected (why: avoid empty chart)
        return prev.filter(n => n !== categoryName)
      }
      return [...prev, categoryName]
    })
  }

  const visibleCategories = useMemo(
    () => categories.filter(c => selectedCategories.includes(c.name)),
    [categories, selectedCategories],
  )

  // --- nicer Y scale with headroom ---
  const computeNiceMax = values => {
    const rawMax = Math.max(10, ...(values.length ? values : [0]))
    const roundUpTo = (n, step) => Math.ceil(n / step) * step
    let maxValue = roundUpTo(rawMax, 2) // 2-pt nice ticks
    if (rawMax / maxValue > 0.9) {
      maxValue = roundUpTo(maxValue * 1.1, 2) // add ~10% headroom if crowded (why: avoid bars hugging the top)
    }
    return maxValue
  }

  const maxValue = computeNiceMax(visibleCategories.map(c => c.currentScore))
  const tickCount = 4 // 4 intervals -> 5 ticks
  const step = maxValue / tickCount
  const yAxisLabels = Array.from({ length: tickCount + 1 }, (_, i) =>
    Math.round(maxValue - step * i),
  )

  if (isLoading) {
    return (
      <div
        className="br"
        style={{
          width: "100%",
          padding: "24px",
          backgroundColor: mode === "dark" ? "#252F40" : "white",
          fontFamily: "Inter, sans-serif",
          boxShadow: "0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
          borderRadius: "12px",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: mode === "dark" ? "white" : "#1F2937",
            marginBottom: "32px",
          }}
        >
          {title}
        </h2>
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <Spinner color="primary" style={{ height: "3rem", width: "3rem" }} />
        </div>
      </div>
    )
  }

  if (categories.length === 0) {
    return (
      <div
        className="br"
        style={{
          width: "100%",
          padding: "24px",
          backgroundColor: mode === "dark" ? "#252F40" : "white",
          fontFamily: "Inter, sans-serif",
          boxShadow: "0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
          borderRadius: "12px",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: mode === "dark" ? "white" : "#1F2937",
            marginBottom: "32px",
          }}
        >
          {title}
        </h2>
        <div
          style={{
            padding: "48px 24px",
            textAlign: "center",
            color: mode === "dark" ? "#9CA3AF" : "#6B7280",
            fontSize: "0.9375rem",
          }}
        >
          Please create a survey campaign on the{" "}
          <Link
            to="/dashboard/campaign"
            style={{ color: mode === "dark" ? "#60A5FA" : "#2990DF", fontWeight: 600 }}
          >
            Campaigns
          </Link>{" "}
          dashboard.
        </div>
      </div>
    )
  }

  return (
    <div
      className="br"
      style={{
        width: "100%",
        padding: "24px",
        display: "flex",
        flexDirection: "column",
        backgroundColor: mode === "dark" ? "#252F40" : "white",
        fontFamily: "Inter, sans-serif",
        boxShadow: "0 10px 15px rgba(0,0,0,0.1), 0 4px 6px rgba(0,0,0,0.05)",
        borderRadius: "12px",
        overflow: "hidden",
      }}
    >
      {/* Header + filter */}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "32px",
        }}
      >
        <h2
          style={{
            fontSize: "1.25rem",
            fontWeight: 600,
            color: mode === "dark" ? "white" : "#1F2937",
          }}
        >
          {title}
        </h2>
        <div style={{ position: "relative" }}>
          <button
            onClick={() => setOpen(p => !p)}
            style={{
              padding: "8px 12px",
              borderRadius: "8px",
              border: "none",
              backgroundColor: "#F3F4F6",
              color: "#6B6B6C",
              cursor: "pointer",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              boxShadow: "0 1px 2px rgba(0,0,0,0.05)",
            }}
          >
            <FilterAltOutlinedIcon />
          </button>

          {open && (
            <div
              style={{
                position: "absolute",
                top: "110%",
                right: 0,
                minWidth: "200px",
                backgroundColor: mode === "dark" ? "#1F2937" : "#fff",
                border:
                  mode === "dark" ? "1px solid #374151" : "1px solid #E5E7EB",
                borderRadius: "8px",
                boxShadow:
                  mode === "dark"
                    ? "0 2px 8px rgba(0,0,0,0.5)"
                    : "0 2px 8px rgba(0,0,0,0.1)",
                zIndex: 50,
                padding: "6px 0",
              }}
            >
              {categories.map(category => (
                <label
                  key={category.id}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "8px 12px",
                    cursor: "pointer",
                    fontSize: "0.875rem",
                    color: selectedCategories.includes(category.name)
                      ? mode === "dark"
                        ? "#fff"
                        : "#111827"
                      : mode === "dark"
                        ? "#D1D5DB"
                        : "#6B7280",
                    borderRadius: "6px",
                    margin: "2px 6px",
                  }}
                >
                  <input
                    type="checkbox"
                    checked={selectedCategories.includes(category.name)}
                    onChange={() => toggleCategory(category.name)}
                  />
                  {category.name === "Timely COnduct"
                    ? "Timely Conduct"
                    : category.name}
                </label>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Chart */}
      <div style={{ position: "relative", paddingTop: "16px" }}>
        {/* Y-axis labels */}
        <div
          style={{
            position: "absolute",
            left: 0,
            top: "16px",
            height: `${CHART_HEIGHT}px`,
            display: "flex",
            flexDirection: "column",
            justifyContent: "space-between",
            fontSize: "0.75rem",
            color: mode === "dark" ? "#9CA3AF" : "#6B7280",
            width: "32px",
            textAlign: "right",
          }}
        >
          {yAxisLabels.map((label, index) => (
            <span key={index}>{label}</span>
          ))}
        </div>

        {/* Plot area */}
        <div style={{ marginLeft: "40px", position: "relative" }}>
          {/* Grid */}
          <div
            style={{
              position: "absolute",
              left: 0,
              right: 0,
              top: 0,
              height: `${CHART_HEIGHT}px`,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              pointerEvents: "none",
            }}
          >
            {yAxisLabels.map((_, index) => (
              <div
                key={index}
                style={{
                  borderTop:
                    mode === "dark" ? "1px solid #374151" : "1px solid #E5E7EB",
                  width: "100%",
                }}
              />
            ))}
          </div>

          {/* Bars */}
          <div
            style={{
              display: "flex",
              alignItems: "flex-start",
              justifyContent: "space-around",
              gap: "24px",
              position: "relative",
            }}
          >
            {visibleCategories.map(category => {
              const barHeightPx =
                (category.currentScore / maxValue) * CHART_HEIGHT
              return (
                <div
                  key={category.id}
                  style={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    maxWidth: "140px",
                    flex: 1,
                  }}
                >
                  {/* fixed-height bar area (baseline is consistent across all columns) */}
                  <div
                    style={{
                      height: `${CHART_HEIGHT}px`,
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "flex-end",
                      position: "relative",
                    }}
                  >
                    <div
                      style={{
                        width: "60%",
                        backgroundColor: "#2990DF",
                        borderTopLeftRadius: "6px",
                        borderTopRightRadius: "6px",
                        transition: "height 0.3s",
                        height: `${barHeightPx}px`,
                        cursor: "pointer",
                        position: "relative",
                      }}
                      onMouseEnter={e => {
                        e.currentTarget.style.opacity = "0.85"
                      }}
                      onMouseLeave={e => {
                        e.currentTarget.style.opacity = "1"
                      }}
                    >
                      {/* score above bar */}
                      <div
                        style={{
                          position: "absolute",
                          top: "-24px",
                          left: "50%",
                          transform: "translateX(-50%)",
                          fontSize: "0.875rem",
                          fontWeight: 600,
                          color: mode === "dark" ? "#fff" : "#111827",
                          whiteSpace: "nowrap",
                        }}
                      >
                        {category.currentScore}
                      </div>
                    </div>
                  </div>

                  {/* X-axis label (outside chart height; never affects baseline) */}
                  <div
                    style={{
                      fontSize: "0.75rem",
                      color: mode === "dark" ? "#D1D5DB" : "#374151",
                      marginTop: "12px",
                      fontWeight: 500,
                      textAlign: "center",
                      wordBreak: "break-word",
                      textTransform: "capitalize",
                    }}
                  >
                    {category.name === "Timely COnduct"
                      ? "Timely Conduct"
                      : category.name}
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      </div>
    </div>
  )
}
